import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Button, Row, Col, Input } from "reactstrap";
import Image from "../Image";
import { ColorGrade, ColorOption, Cosmetic } from "../../models/Cosmetic";
import { cosmeticService } from "../../services/cosmeticService";
import { categories } from "../../enums/CategoriesEnum";
import { types } from "../../enums/TypesEnum";
import { accountService } from "../../services/accountService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faHeart,
  faShoppingBag,
  faStar,
  faAngry,
  faSave,
  faEdit,
} from "@fortawesome/free-solid-svg-icons";
import CreateShortModal from "../modals/CreateShortModal";
import CosmeticProfileGrade from "./CosmeticProfileGrade";

const CosmeticProfile = () => {
  const location = useLocation();
  const guid = location.pathname.substring(
    location.pathname.lastIndexOf("/") + 1
  );
  const user = accountService.userValue;

  const [cosmetic, setCosmetic] = useState<Cosmetic>();
  const [loading, setLoading] = useState<boolean>(true);
  const [createModal, setCreateModal] = useState<boolean>(false);
  const [enableDescriptionUpdate, setEnableDescriptionUpdate] =
    useState<boolean>(false);
  const [cosmeticDescription, setCosmeticDescription] = useState<string>();

  const populateData = async () => {
    let cos = await cosmeticService.getCosmetic(guid);
    setCosmetic(cos);
    setCosmeticDescription(cos.description);
    setLoading(false);
  };

  useEffect(() => {
    populateData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const language = "pl";

  const addLike = async () => {
    await cosmeticService.addLike(cosmetic?.guid || "");
    if (cosmetic) {
      if (!cosmetic.likes) {
        cosmetic.likes = [];
      }
      const index = cosmetic.likes.indexOf(user?.userName || "");
      if (index === -1) {
        cosmetic.likes.push(user?.userName || "");
      } else {
        cosmetic.likes.splice(index, 1);
      }
      setCosmetic({ ...cosmetic });
    }
  };

  const addDislike = async () => {
    await cosmeticService.addDisike(cosmetic?.guid || "");
    if (cosmetic) {
      if (!cosmetic.dislikes) {
        cosmetic.dislikes = [];
      }
      const index = cosmetic.dislikes.indexOf(user?.userName || "");
      if (index === -1) {
        cosmetic.dislikes.push(user?.userName || "");
      } else {
        cosmetic.dislikes.splice(index, 1);
      }
      setCosmetic({ ...cosmetic });
    }
  };

  const updateComment = async (
    guid: string,
    color: string,
    updatedComment: string
  ) => {
    const updatedCosmetic = await cosmeticService.updateReviewComment(
      guid,
      color,
      updatedComment
    );
    setCosmetic(updatedCosmetic);
  };

  const addToBag = async () => {
    await cosmeticService.addBag(cosmetic?.guid || "");
    if (cosmetic) {
      if (!cosmetic.bag) {
        cosmetic.bag = [];
      }
      const index = cosmetic.bag.indexOf(user?.userName || "");
      if (index === -1) {
        cosmetic.bag.push(user?.userName || "");
      } else {
        cosmetic.bag.splice(index, 1);
      }
      setCosmetic({ ...cosmetic });
    }
  };

  const sortGrades = (grades: ColorGrade[]) => {
    let sorted = grades.sort(
      (a, b) =>
        new Date(b.createDate).getTime() - new Date(a.createDate).getTime()
    );
    const userComment = sorted.find((x) => x.username === user?.userName);
    if (userComment) {
      sorted = sorted.filter((x) => x.username !== user?.userName);
      sorted.unshift(userComment);
    }
    return sorted;
  };

  const updateDescription = async (guid: string, description: string) => {
    await cosmeticService.updateDescription(guid, description);
    if (cosmetic) {
      cosmetic.description = description;
      setCosmetic({ ...cosmetic });
    }
  };

  const saveDescription = async () => {
    if (cosmetic?.guid && cosmeticDescription) {
      await updateDescription(cosmetic.guid, cosmeticDescription);
      setEnableDescriptionUpdate(!enableDescriptionUpdate);
    } else {
      //TODO:Alert user
    }
  };

  return (
    <div>
      {cosmetic && (
        <CreateShortModal
          toggle={createModal}
          setToggle={setCreateModal}
          cosmetic={cosmetic}
          setCosmetic={setCosmetic}
          name={cosmetic.name}
          brand={cosmetic.brand}
          type={cosmetic.type}
          category={cosmetic.category}
        />
      )}
      {loading ? (
        <div className="spinner" />
      ) : (
        <div className="wrap">
          <div className="shadow-box">
            <Row>
              <Col className="cosmetic-tile-pic">
                <Row style={{ justifyContent: "center" }}>
                  <Image
                    guid={cosmetic?.guid || ""}
                    category={cosmetic?.category}
                    width={300}
                  />
                </Row>
                <div className="d-flex" style={{ justifyContent: "center" }}>
                  <span
                    title="dodaj do ulubionych"
                    className="icon-style"
                    onClick={addLike}
                  >
                    <FontAwesomeIcon
                      color="pink"
                      className="icon-style-font"
                      icon={faHeart}
                    />
                    <p style={{ textAlign: "center" }}>
                      {cosmetic?.likes?.length ?? 0}
                    </p>
                  </span>
                  <span
                    title="dodaj do kosmetyczki"
                    className="icon-style"
                    onClick={addToBag}
                  >
                    <FontAwesomeIcon
                      color="#6c88c4"
                      className="icon-style-font"
                      icon={faShoppingBag}
                    />
                    <p style={{ textAlign: "center" }}>
                      {cosmetic?.bag?.length ?? 0}
                    </p>
                  </span>
                  <span
                    title="dodaj do nielubianych"
                    className="icon-style"
                    onClick={addDislike}
                  >
                    <FontAwesomeIcon
                      color="#74737a"
                      className="icon-style-font"
                      icon={faAngry}
                    />
                    <p style={{ textAlign: "center" }}>
                      {cosmetic?.dislikes?.length ?? 0}
                    </p>
                  </span>
                </div>
              </Col>
              <Col className="cosmetic-tile-description">
                <h6>
                  {cosmetic?.brand} -{" "}
                  {categories
                    .find((x) => x.value === cosmetic?.category)
                    ?.translations.find((x) => x.language === language)?.name ??
                    cosmetic?.category}{" "}
                  -{" "}
                  {
                    types
                      .find((x) => x.category === cosmetic?.category)
                      ?.values.find((xx) => xx.value === cosmetic?.type)
                      ?.translations.find((xxx) => xxx.language === language)
                      ?.name
                  }
                </h6>
                <h4 style={{ fontWeight: "bold", padding: "10" }}>
                  {cosmetic?.name}
                </h4>
                <h5>
                  Ocena <FontAwesomeIcon color="lightgrey" icon={faStar} />{" "}
                  <b>{cosmetic?.averageGrade}</b>
                </h5>
                <h5>Opis produktu</h5>
                {!enableDescriptionUpdate ? (
                  <div>
                    <p style={{ textAlign: "justify" }}>
                      {cosmetic?.description}
                    </p>
                    {user?.role === 2 && (
                      <FontAwesomeIcon
                        onClick={() =>
                          setEnableDescriptionUpdate(!enableDescriptionUpdate)
                        }
                        color="lightgrey"
                        style={{
                          fontSize: 15,
                          marginLeft: 5,
                          cursor: "pointer",
                        }}
                        icon={faEdit}
                      />
                    )}
                  </div>
                ) : (
                  <div>
                    <Input
                      style={{ resize: "none", minHeight: "50vh" }}
                      onChange={(e) => setCosmeticDescription(e.target.value)}
                      type="textarea"
                      value={cosmeticDescription}
                    />
                    <FontAwesomeIcon
                      onClick={saveDescription}
                      color="lightgrey"
                      style={{ fontSize: 15, marginLeft: 5, cursor: "pointer" }}
                      icon={faSave}
                    />
                  </div>
                )}
              </Col>
              <Col>
                <Row>
                  <Col>
                    <h5>Komentarze</h5>
                  </Col>
                  <Col>
                    {user && (
                      <Button
                        style={{ padding: 10, width: "100%" }}
                        onClick={() => setCreateModal(true)}
                      >
                        <FontAwesomeIcon
                          color="lightgrey"
                          style={{ marginRight: 5 }}
                          icon={faPlus}
                        />
                        Dodaj
                      </Button>
                    )}
                  </Col>
                </Row>
                <div>
                  {cosmetic?.colors.map((color: ColorOption) => (
                    <div key={color.name}>
                      <div>
                        <p>{color.name === "default" ? "" : color.name}</p>
                        <table
                          className="table table-striped"
                          aria-labelledby="tabelLabel"
                        >
                          <tbody>
                            {sortGrades(color.grades).map(
                              (grade: ColorGrade, index) => (
                                <CosmeticProfileGrade
                                  key={index}
                                  grade={grade}
                                  guid={guid}
                                  color={color.name}
                                  user={user}
                                  update={updateComment}
                                />
                              )
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          </div>
        </div>
      )}
    </div>
  );
};

export default CosmeticProfile;
